import React, { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ScriptCreationProvider, useScriptCreation } from '../../contexts/ScriptCreationContext';
import { Icon } from '../common/Icon';
import { FaSave, FaCheck, FaTrash } from 'react-icons/fa';
import { scriptService } from '../../services/scriptService';

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 15px;
  margin-bottom: 10px;
`;

const Title = styled.h1`
  color: #ffffff;
  margin: 0;
`;

const DraftLink = styled(Link)`
  color: #007bff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 400;
  
  &:hover {
    color: #0056b3;
    text-decoration: underline;
  }
`;

const SegmentedControl = styled.div`
  display: flex;
  background-color: #2a2a2a;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 30px;
`;

const SegmentButton = styled.button<{ $active: boolean; disabled?: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  background-color: ${props => props.$active ? '#007bff' : 'transparent'};
  color: ${props => props.disabled ? '#666' : '#ffffff'};
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background-color: ${props => props.$active ? '#0056b3' : '#333'};
  }
`;

const SaveSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const ScriptNameInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SaveButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #c82333;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

// Modal components
const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 12px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: #ffffff;
  margin: 0 0 15px 0;
  font-size: 1.3rem;
  font-weight: 600;
`;

const ModalText = styled.p`
  color: #cccccc;
  margin: 0 0 25px 0;
  line-height: 1.5;
  font-size: 1rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  min-width: 80px;
  
  ${props => props.$variant === 'danger' ? `
    background-color: #dc3545;
    color: white;
    &:hover {
      background-color: #c82333;
      transform: translateY(-1px);
    }
  ` : `
    background-color: #666;
    color: white;
    &:hover {
      background-color: #777;
      transform: translateY(-1px);
    }
  `}
`;

const ScriptName = styled.span`
  font-weight: 600;
  color: #ffffff;
`;

interface ScriptCreationLayoutContentProps {}

const ScriptCreationLayoutContent: React.FC<ScriptCreationLayoutContentProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    scriptName,
    setScriptName,
    isEditingExisting,
    draftCount,
    selectedParts,
    isScriptSaved,
    saveScript,
    savePublishData,
    hasAccessedSpeech,
    shouldOpenToSpeech,
    setShouldOpenToSpeech,
    shouldOpenToAudio,
    setShouldOpenToAudio,
    shouldOpenToPublish,
    setShouldOpenToPublish,
    scriptAudioFile,
    scriptFinalAudioFile,
    scriptDbId,
    zenSettings,
    shouldOpenToZenOptions,
    setShouldOpenToZenOptions,
    shouldOpenToZenPreview,
    setShouldOpenToZenPreview,
    shouldOpenToZenSummary,
    setShouldOpenToZenSummary
  } = useScriptCreation();

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Dynamically determine the base path based on current location
  const getBasePath = useCallback(() => {
    const pathSegments = location.pathname.split('/');
    if (pathSegments[1] === 'edit' && pathSegments[2]) {
      return `/edit/${pathSegments[2]}`;
    }
    return '/create';
  }, [location.pathname]);

  // Auto-navigate to Speech page if script has segments
  useEffect(() => {
    if (shouldOpenToSpeech && isEditingExisting) {
      const basePath = getBasePath();
      const speechPath = `${basePath}/speech`;
      
      // Only navigate if we're not already on the Speech page
      if (location.pathname !== speechPath) {
        console.log('Navigating to Speech page for script with segments');
        navigate(speechPath, { replace: true });
      }
      
      // Reset the flag after navigation
      setShouldOpenToSpeech(false);
    }
  }, [shouldOpenToSpeech, isEditingExisting, location.pathname, navigate, setShouldOpenToSpeech]);

  // Auto-navigate to Audio page if script has audio file
  useEffect(() => {
    if (shouldOpenToAudio && isEditingExisting) {
      const basePath = getBasePath();
      const audioPath = `${basePath}/audio`;
      
      // Only navigate if we're not already on the Audio page
      if (location.pathname !== audioPath) {
        console.log('Navigating to Audio page for script with audio file');
        navigate(audioPath, { replace: true });
      }
      
      // Reset the flag after navigation
      setShouldOpenToAudio(false);
    }
  }, [shouldOpenToAudio, isEditingExisting, location.pathname, navigate, setShouldOpenToAudio]);

  // Auto-navigate to Publish page if script has final audio file
  useEffect(() => {
    if (shouldOpenToPublish && isEditingExisting) {
      const basePath = getBasePath();
      const publishPath = `${basePath}/publish`;
      
      // Only navigate if we're not already on the Publish page
      if (location.pathname !== publishPath) {
        console.log('Navigating to Publish page for script with final audio file');
        navigate(publishPath, { replace: true });
      }
      
      // Reset the flag after navigation
      setShouldOpenToPublish(false);
    }
  }, [shouldOpenToPublish, isEditingExisting, location.pathname, navigate, setShouldOpenToPublish]);

  // Auto-navigate to Zen pages for Intelligent Zen Mode scripts
  useEffect(() => {
    if (shouldOpenToZenOptions && isEditingExisting) {
      const zenPath = `${getBasePath()}/zen`;
      if (location.pathname !== zenPath) {
        navigate(zenPath, { replace: true });
      }
      setShouldOpenToZenOptions(false);
    }
  }, [shouldOpenToZenOptions, isEditingExisting, location.pathname, navigate, setShouldOpenToZenOptions, getBasePath]);

  useEffect(() => {
    if (shouldOpenToZenPreview && isEditingExisting) {
      const zenPath = `${getBasePath()}/zen-preview`;
      if (location.pathname !== zenPath) {
        navigate(zenPath, { replace: true });
      }
      setShouldOpenToZenPreview(false);
    }
  }, [shouldOpenToZenPreview, isEditingExisting, location.pathname, navigate, setShouldOpenToZenPreview, getBasePath]);

  useEffect(() => {
    if (shouldOpenToZenSummary && isEditingExisting) {
      const zenPath = `${getBasePath()}/zen-summary`;
      if (location.pathname !== zenPath) {
        navigate(zenPath, { replace: true });
      }
      setShouldOpenToZenSummary(false);
    }
  }, [shouldOpenToZenSummary, isEditingExisting, location.pathname, navigate, setShouldOpenToZenSummary, getBasePath]);

  const basePath = getBasePath();

  const isZenMode = !!zenSettings?.enabled;

  const segments = isZenMode ? [
    { key: 'plan', label: 'Plan', path: `${basePath}/plan` },
    { key: 'zen', label: 'Zen Options', path: `${basePath}/zen` },
    { key: 'zen-preview', label: 'Preview', path: `${basePath}/zen-preview` },
    { key: 'zen-summary', label: 'Summary', path: `${basePath}/zen-summary` },
    { key: 'publish', label: 'Publish', path: `${basePath}/publish` }
  ] : [
    { key: 'plan', label: 'Plan', path: `${basePath}/plan` },
    { key: 'write', label: 'Write', path: `${basePath}/write` },
    { key: 'speech', label: 'Speech', path: `${basePath}/speech` },
    { key: 'audio', label: 'Audio', path: `${basePath}/audio` },
    { key: 'publish', label: 'Publish', path: `${basePath}/publish` }
  ];

  const currentSegment = segments.find(s => location.pathname === s.path) || segments[0];

  const handleSegmentClick = (segment: typeof segments[0]) => {
    // Disable navigation to write/speech/audio/publish if no parts selected
    if (segment.key !== 'plan' && selectedParts.length === 0) {
      return;
    }
    
    navigate(segment.path);
  };

  const handleScriptNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScriptName(e.target.value);
  };

  // Determine which save function to use based on current page
  const handleSave = async () => {
    const currentPath = location.pathname;
    if (currentPath.includes('/publish')) {
      await savePublishData();
    } else {
      await saveScript();
    }
  };

  const isSegmentDisabled = (segmentKey: string) => {
    if (segmentKey === 'plan') return false;
    if (selectedParts.length === 0) return true;

    // Intelligent Zen Mode gating (stage-based)
    if (isZenMode) {
      const stage = zenSettings?.stage;
      if (segmentKey === 'zen') return false;
      if (segmentKey === 'zen-preview') return !(stage === 'script_generated' || stage === 'speech_generated' || stage === 'mix_generated');
      if (segmentKey === 'zen-summary') return stage !== 'mix_generated';
      if (segmentKey === 'publish') return !scriptFinalAudioFile;
      return false;
    }

    // Check if this is an unguided meditation (only selfguided part selected)
    const isUnguided = selectedParts.length === 1 && selectedParts.includes('selfguided');
    
    if (isUnguided) {
      // For unguided meditations: skip write and speech, allow audio directly
      if (segmentKey === 'write' || segmentKey === 'speech') return true;
      if (segmentKey === 'audio') return false; // Always enable audio for unguided
      if (segmentKey === 'publish' && !scriptFinalAudioFile) return true;
    } else {
      // For guided meditations: normal flow
      if (segmentKey === 'speech' && !hasAccessedSpeech) return true;  // Disable until accessed from Write
      if (segmentKey === 'audio' && !scriptAudioFile) return true; // Enable if script has audio file
      if (segmentKey === 'publish' && !scriptFinalAudioFile) return true; // Enable if script has final audio file
    }
    
    return false;
  };

  const handleDeleteScript = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteScript = async () => {
    if (!scriptDbId) return;
    
    setIsDeleting(true);
    try {
      await scriptService.deleteScript(scriptDbId);
      setShowDeleteModal(false);
      // Navigate back to the You page after successful deletion
      navigate('/you?tab=drafts');
    } catch (err) {
      console.error('Error deleting script:', err);
      alert('Failed to delete script. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteScript = () => {
    setShowDeleteModal(false);
  };

  return (
    <Container>
      <Header>
        <div>
          <TitleContainer>
            <Title>{isEditingExisting ? 'YouMeditation Studio' : 'Create Your Meditation'}</Title>
            {!isEditingExisting && draftCount > 0 && currentSegment.key === 'plan' && (
              <DraftLink to="/you?tab=drafts">(or finish one you started)</DraftLink>
            )}
            {draftCount > 0 && currentSegment.key != 'plan' && (
              <DraftLink to="/you?tab=drafts">(My Drafts)</DraftLink>
            )}
          </TitleContainer>
        </div>
        
        <SaveSection>
          <ScriptNameInput
            type="text"
            value={scriptName}
            onChange={handleScriptNameChange}
            placeholder="Enter script name..."
          />
          <SaveButton
            onClick={handleSave}
            disabled={!scriptName.trim() || isScriptSaved}
            title={isScriptSaved ? 'Saved!' : 'Save script'}
          >
            <Icon icon={isScriptSaved ? FaCheck : FaSave} />
            {isScriptSaved ? 'Saved!' : 'Save'}
          </SaveButton>
          {isEditingExisting && (
            <DeleteButton
              onClick={handleDeleteScript}
              disabled={isDeleting}
              title="Delete script"
            >
              <Icon icon={FaTrash} />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </DeleteButton>
          )}
        </SaveSection>
      </Header>

      <SegmentedControl>
        {segments.map((segment) => (
          <SegmentButton
            key={segment.key}
            $active={currentSegment.key === segment.key}
            disabled={isSegmentDisabled(segment.key)}
            onClick={() => handleSegmentClick(segment)}
          >
            {segment.label}
          </SegmentButton>
        ))}
      </SegmentedControl>

      <Outlet />

      {/* Delete Confirmation Modal */}
      <Modal $isOpen={showDeleteModal}>
        <ModalContent>
          <ModalTitle>Delete Script</ModalTitle>
          <ModalText>
            Are you sure you want to delete <ScriptName>"{scriptName}"</ScriptName>? 
            This action cannot be undone.
          </ModalText>
          <ModalButtons>
            <ModalButton onClick={cancelDeleteScript} disabled={isDeleting}>
              Cancel
            </ModalButton>
                          <ModalButton $variant="danger" onClick={confirmDeleteScript} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </ModalButton>
          </ModalButtons>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export const ScriptCreationLayout: React.FC = () => {
  return (
    <ScriptCreationProvider>
      <ScriptCreationLayoutContent />
    </ScriptCreationProvider>
  );
}; 